import { FunctionComponent } from "react";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { LeadScoringPopover } from "@/components/LeadScoringPopover/LeadScoringPopover";

import { PersonaCategoryResults } from "@/pages/Private/pages/Apollo/schema/get-people";

import { classNames } from "@/utils/classNames";

/**
 * Compact persona-priority badge (P1 / P2 / P3 / DQ / ?) derived from a
 * prospect's `scorePersonaCategoryResult.overallScore`. Mirrors the colour
 * mapping of `renderCurrentScoreResult` (List columns) but is self-contained
 * and Tailwind-only so it is safe to reuse inside the Chrome extension's shadow
 * root (no MUI / portal dependencies).
 */
export type ScoreBadgeSize = "sm" | "md";
export type ScoreBadgeVariant = "outline" | "solid";

const toScoreLabel = (overallScore?: string | null): string => {
	switch (overallScore) {
		case "1":
			return "P1";
		case "2":
			return "P2";
		case "3":
			return "P3";
		case "Disqualified":
			return "DQ";
		case "Unsure":
			return "?";
		default:
			return "?";
	}
};

const scoreColorClasses = (label: string): string => {
	switch (label) {
		case "P1":
			return "text-text-success border border-text-success bg-background-success";
		case "P2":
			return "text-text-warning border border-text-warning bg-background-warning";
		case "P3":
			return "border border-error text-error bg-error-light";
		case "DQ":
			return "text-white bg-error";
		default:
			return "text-text-meta bg-border-light";
	}
};

/** Solid colour-coded fill (unified-prototype v33 `lid-score-*`) — used when the
 *  badge overlays an avatar, where the outline style reads as washed out. */
const solidScoreColorClasses = (label: string): string => {
	switch (label) {
		case "P1":
			return "bg-score-p1 text-white";
		case "P2":
			return "bg-score-p2 text-white";
		case "P3":
			return "bg-score-p3 text-white";
		case "DQ":
			return "bg-score-dq text-white";
		default:
			return "bg-placeholder text-white";
	}
};

const sizeClasses: Record<ScoreBadgeSize, string> = {
	sm: "w-[20px] h-[20px] text-[9px]",
	md: "w-[24px] h-[24px] text-xxs",
};

export const ProspectScoreBadge: FunctionComponent<{
	overallScore?: string | null;
	reason?: string | null;
	/**
	 * When provided, the hover shows the beautified structured Lead Scoring card
	 * (PREC-3499) instead of the plain `reason` text.
	 */
	scoring?: PersonaCategoryResults | null;
	size?: ScoreBadgeSize;
	variant?: ScoreBadgeVariant;
	className?: string;
}> = ({ overallScore, reason, scoring, size = "md", variant = "outline", className }) => {
	const label = toScoreLabel(overallScore);

	const badge = (
		<div
			className={classNames(
				"flex shrink-0 items-center justify-center rounded-full font-bold",
				sizeClasses[size],
				variant === "solid" ? solidScoreColorClasses(label) : scoreColorClasses(label),
				className
			)}
			data-testid="prospect-score-badge"
		>
			{label}
		</div>
	);

	if (scoring) {
		if (!scoring.leadScoringBreakdown && !scoring.overallReasoning) {
			return badge;
		}

		return (
			<Tooltip
				content={<LeadScoringPopover scoreLabel={label} scoring={scoring} />}
				delay={300}
				panel
			>
				{badge}
			</Tooltip>
		);
	}

	if (!reason) {
		return badge;
	}

	return (
		<Tooltip content={reason} delay={300}>
			{badge}
		</Tooltip>
	);
};
